import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { getL1CategoryIdBySeoName } from '@gumtree/shared/src/util/category-mapping';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4AndNavigate, trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { Link } from '@gumtree/ui-library';
import { StyledAvatar } from '@gumtree/message-centre/src/components/ui-atoms/avatar/style';
import { genStaticStars } from '@gumtree/message-centre/src/components/chat/shared/rating-stars/rating-stars';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';

import {
    SellerBlockContainer,
    RatingStarContainer,
    RatingStarContainerTotal,
    SellerNameContainer,
    PostingForWithBadges,
    PostingForContainer,
    PostingLength,
    JobImage,
    SellerNameRating,
    WebsiteLink,
} from './index.style';
import ClickOutIcon from './click-out-icon.svg';
import SellerStats from '../seller-stats';
import { VipState } from '../../reducers';
import { matchSellerInfoWithCategory } from '../../get-data/shouldFetchVipSellerInfo';

export default function SellerAvatarBlock() {
    const { adDetails, images, sellerStats, sellerRating, vipSellerInfo, sellerContactDetails } =
        useSelector(
            ({
                adDetails,
                sellerRating,
                images,
                sellerStats,
                vipSellerInfo,
                sellerContactDetails,
            }: VipState) => ({
                adDetails,
                sellerRating,
                images,
                sellerStats,
                vipSellerInfo,
                sellerContactDetails,
            }),
            shallowEqual
        );

    const { postingSince, l1Category, logoUrl, sellerOtherAds, isFromMotorsFeed, pro, sellerType } = adDetails;
    const { average, contactName, total, accountPublicId } = sellerRating;

    const isMotors = l1Category === getL1CategoryIdBySeoName('cars-vans-motorbikes');
    const isShowReviewInfo = matchSellerInfoWithCategory({l1Category, sellerType});
    const inJobsCategory = l1Category === getL1CategoryIdBySeoName('jobs');
    const showRating = Boolean(isShowReviewInfo && average);
    const accountUrl = accountPublicId
        ? inJobsCategory
            ? sellerOtherAds
            : `/profile/account/${accountPublicId}`
        : null;

    const noRatingCategory = isShowReviewInfo ? false : !showRating && isShowReviewInfo;

    const handleViewProfile = (e) => {
        trackV2('ViewProfileBegin');
        trackGA4AndNavigate<GA4.ReviewUserProfile>({
            mouseEvent: e,
            href: accountUrl!,
            payload: {
                event: 'click_to_view_seller_profile',
                linkDomain: window.location.hostname,
                linkURL: accountUrl!,
                linkText: 'View Profile',
                linkSection: 'Seller Information Block',
                listingDetails: {}, // Already available because we're on VIP
            },
        });
    };

    return (
        <SellerBlockContainer
            isShowReviewInfo={isShowReviewInfo}
            noRating={!average}
            darkBackground={!isShowReviewInfo && (showRating || inJobsCategory)}
            noRatingCategory={noRatingCategory}
            hasLogo={Boolean(logoUrl) || (inJobsCategory && images?.length > 0)}
            otherAds={!showRating && !isShowReviewInfo && !inJobsCategory}
        >
            {isShowReviewInfo ? (
                <a href={accountUrl!!} data-q="view-profile-link" onClick={handleViewProfile}>
                    {logoUrl ? (
                        <div className="vip-cta-logo">
                            <img src={logoUrl} alt="vip logo" />
                        </div>
                    ) : null}
                    <SellerNameRating isJobs={inJobsCategory}>
                        <StyledAvatar size="small" isLink={false}>
                            {contactName?.charAt(0)}
                        </StyledAvatar>
                        <div className={`seller-name-rating-bundle ${!average ? `no-rating` : ''}`}>
                            <SellerName contactName={contactName} />
                            <RatingsView average={average} total={total} />
                        </div>
                        <SellerStats
                            showRating={showRating}
                            sellerStats={sellerStats}
                            vipSellerInfo={vipSellerInfo}
                        />
                    </SellerNameRating>
                </a>
            ) : (
                <>
                    {logoUrl ? (
                        <div className="vip-cta-logo">
                            <img src={logoUrl} alt="vip logo" />
                        </div>
                    ) : inJobsCategory ? (
                        images?.length > 0 && (
                            <JobImage>
                                <img src={images[0]} alt="recruiter logo" />
                            </JobImage>
                        )
                    ) : null}
                    <SellerNameRating isJobs={inJobsCategory}>
                        <div className={`seller-name-rating-bundle ${!average ? `no-rating` : ''}`}>
                            <SellerName contactName={contactName} />
                            {
                                l1Category === getL1CategoryIdBySeoName('jobs')
                                ? <JobsView accountUrl={accountUrl} />
                                : <DefaultCategoryView
                                        isMotors={isMotors}
                                        postingSince={postingSince}
                                        sellerOtherAds={sellerOtherAds}
                                        websiteUrl={sellerContactDetails.websiteUrl}
                                        isFromMotorsFeed={isFromMotorsFeed}
                                        isProAccount={pro}
                                    />}
                        </div>
                    </SellerNameRating>
                </>
            )}
        </SellerBlockContainer>
    );
}

function SellerName(props: { contactName: string }) {
    const { contactName } = props;
    return (
        <SellerNameContainer>
            <h2 className="truncate-line seller-rating-block-name">{contactName}</h2>
        </SellerNameContainer>
    );
}

export function RatingsView(props: RatingsViewProps) {
    const { average, total } = props;

    return (
        <>
            {average && average > 0 ? (
                <RatingStarContainer>
                    <RatingStarContainerTotal>
                        {genStaticStars(average!!, 'blade')}
                        <span className="count">({total})</span>
                    </RatingStarContainerTotal>
                </RatingStarContainer>
            ) : null}
        </>
    );
}

interface RatingsViewProps {
    average: number | undefined;
    total: number | undefined;
}

function JobsView({ accountUrl }: { accountUrl: string | null }) {
    return accountUrl ? (
        <RatingStarContainer>
            <Link href={accountUrl} text="View Profile" />
        </RatingStarContainer>
    ) : null;
}

function DefaultCategoryView({
    postingSince,
    sellerOtherAds,
    websiteUrl,
    isMotors,
    isFromMotorsFeed,
    isProAccount,
}: {
    postingSince: string;
    sellerOtherAds: string;
    websiteUrl: string | undefined;
    isMotors: boolean;
    isFromMotorsFeed: boolean;
    isProAccount: boolean;
}) {
    return (
        <PostingForWithBadges className="posting-length">
            <PostingForContainer>
                <PostingLength>Posting for {postingSince}</PostingLength>
                {sellerOtherAds !== '' && (
                    <Link
                        as={isProAccount ? 'a' : 'button'}
                        href={sellerOtherAds}
                        text="See all ads"
                        dataQ="seller-see-all-ads"
                    />
                )}
            </PostingForContainer>
            {websiteUrl && isMotors && !isFromMotorsFeed && (
                <Link
                    openInNewTab
                    noFollow
                    href={websiteUrl}
                    onClick={async () => {
                        trackGA4Event<GA4.ContactSellerExternalEvent>({
                            event: 'contact_seller_external',
                            linkDomain: getDomainFromUrl(websiteUrl),
                            linkURL: websiteUrl,
                            linkText: 'Visit website',
                            formValidation: 'success',
                            ...(isFromMotorsFeed ? { urlPartner: 'emg' } : {}),
                        });
                    }}
                    dataQ="seller-websiteUrl"
                >
                    <WebsiteLink>
                        <ClickOutIcon /> <span>Visit website</span>
                    </WebsiteLink>
                </Link>
            )}
        </PostingForWithBadges>
    );
}
